import i18n from '@/i18n'
import { getAtPath } from './configPaths'
/**
 * Re-groups a schema's sections into the cards declared by `schema.ui.groups`.
 *
 * Presentation metadata is optional and additive: a schema without `ui` returns null
 * and SchemaForm falls back to rendering one card per schema section, which is what
 * keeps scratch-to-reveal (schema-v2) and every pre-2.3.0 split-slider release working.
 *
 * Anything the schema does not assign to a group is preserved in `leftovers` rather
 * than dropped — a template must never lose a field because the design forgot it.
 */

export function buildUiLayout(schema) {
  const defs = schema && schema.ui && schema.ui.groups
  if (!Array.isArray(defs) || defs.length === 0) return null

  const groups = defs.map((group) => ({ ...group, fields: [], sections: [] }))
  const byId = new Map(groups.map((group) => [group.id, group]))
  const leftovers = []

  for (const section of schema.sections || []) {
    // Collection sections (slides) keep their own editor; they just move card.
    if (section.collection) {
      const target = byId.get(section.ui && section.ui.group)
      if (target) target.sections.push(section)
      else leftovers.push(section)
      continue
    }

    const ungrouped = []
    for (const field of section.fields || []) {
      const target = byId.get(field.ui && field.ui.group)
      if (target) target.fields.push(field)
      else ungrouped.push(field)
    }
    if (ungrouped.length) leftovers.push({ ...section, fields: ungrouped })
  }

  for (const group of groups) {
    group.fields.sort((a, b) => uiOrder(a) - uiOrder(b))
    group.sections.sort((a, b) => uiOrder(a) - uiOrder(b))
  }

  return {
    groups: groups.filter((group) => group.fields.length > 0 || group.sections.length > 0),
    leftovers,
  }
}

// Package V2 keeps format and presets in its envelope. For packages opting into
// the established cards, place those existing controls alongside the other fields.
// The AD-209 editor shows the format switcher above the preview instead (withFormat: false).
export function groupedSetupSchema(schema, manifest, { withFormat = true } = {}) {
  if (schema?.ui?.setup !== 'grouped') return schema
  return {
    ...schema,
    sections: [...schema.sections, {
      id: 'template-setup', label: i18n.t('editorV2.core.setup.templateSetup'), fields: [
        ...(withFormat ? [{
          path: 'format.id', type: 'segmented', label: i18n.t('editorV2.core.setup.adFormat'),
          options: (manifest.formats || []).map(({ id, label }) => ({ value: id, label })),
          ui: { group: 'general', order: 20, width: 'full' },
        }] : []),
        {
          path: 'presetId', type: 'select', label: i18n.t('editorV2.core.setup.stylePreset'),
          options: (manifest.presets || []).map(({ id, label, formatId }) => ({
            value: id, label, ...(formatId ? { formats: [formatId] } : {}),
          })),
          ui: { group: 'advanced', order: 10, width: 'full' },
        },
      ],
    }],
  }
}

function uiOrder(node) {
  const order = node && node.ui && node.ui.order
  return typeof order === 'number' ? order : Number.MAX_SAFE_INTEGER
}

/**
 * Builds the URL for an option-card thumbnail held in the template package.
 *
 * Deliberately extension-less — nginx matches `location ~* \.(svg|woff2|…)$` ahead of
 * PHP and serves those from public/ with try_files, so a ".svg" URL 404s before it
 * reaches Laravel. See PreviewController::templateThumb.
 */
export function templateThumbUrl(releaseKey, thumbBase, value, configuration = null) {
  if (!releaseKey || !thumbBase || value === null || value === undefined) return null
  const resolved = String(thumbBase).replace(/\{([A-Za-z0-9_.]+)\}/g, (match, key) => String(getAtPath(configuration, key, '') ?? ''))
  const base = resolved.replace(/\/{2,}/g, '/').replace(/\/+$/, '').replace(/^\/+/, '').replace(/^assets\//, '').replace(/^ui\//, '')
  if (!/^[A-Za-z0-9/_-]+$/.test(base) || !/^[A-Za-z0-9._-]+$/.test(String(value))) return null
  return `/studio-editor-v2/template-thumbs/${encodeURIComponent(releaseKey)}/${base}/${value}`
}
